<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, shallowRef, useTemplateRef } from "vue"
import { storeToRefs } from "pinia"
import { ContextMenuContent, ContextMenuPortal, ContextMenuRoot, ContextMenuTrigger } from "reka-ui"
import { useAppStore } from "@/store/app.store"
import { useContextMenuStore } from "@/store/context-menu.store"
import InputClipboardContextMenu from "@/components/inputs/InputClipboardContextMenu.vue"

const props = defineProps<{
  /** The element that originated the context-menu event. */
  target?: EventTarget
  /** Viewport location of the originating context-menu event. */
  position?: {
    x: number
    y: number
  }
}>()

const emit = defineEmits<{
  closeMenu: []
}>()

defineOptions({
  inheritAttrs: false
})

const appStore = useAppStore()
const contextMenuStore = useContextMenuStore()
const { cursorX, cursorY } = storeToRefs(appStore)

const inputTarget = shallowRef<HTMLInputElement | HTMLTextAreaElement | HTMLElement | null>(null)
if (props.target instanceof HTMLInputElement || props.target instanceof HTMLTextAreaElement) {
  inputTarget.value = props.target
} else if (props.target instanceof HTMLElement) {
  const contentEditable = props.target.closest<HTMLElement>("[contenteditable='true']")
  if (contentEditable?.isContentEditable) {
    inputTarget.value = contentEditable
  }
}

const trigger = useTemplateRef<{ $el: HTMLElement }>("trigger")
const isOpen = shallowRef(false)
const initialCursorX = props.position?.x ?? cursorX.value
const initialCursorY = props.position?.y ?? cursorY.value

function onOpenChange(open: boolean) {
  contextMenuStore.isContextMenuOpen = open

  if (isOpen.value && !open) {
    emit("closeMenu")
  }

  isOpen.value = open
}

onMounted(async () => {
  await nextTick()

  trigger.value?.$el.dispatchEvent(
    new MouseEvent("contextmenu", {
      bubbles: false,
      cancelable: true,
      clientX: initialCursorX,
      clientY: initialCursorY
    })
  )
})

onUnmounted(() => {
  contextMenuStore.isContextMenuOpen = false
})
</script>

<template>
  <ContextMenuRoot :modal="false" @update:open="onOpenChange">
    <ContextMenuTrigger ref="trigger" aria-hidden="true" />
    <ContextMenuPortal>
      <ContextMenuContent
        class="context-menu-wrapper divide-gray-lighten-2 surface border-gray-lighten-1 z-50 min-w-64 divide-y rounded-md border focus:outline-hidden"
        :class="$attrs.class"
      >
        <InputClipboardContextMenu v-if="inputTarget" v-model="inputTarget" />
        <slot />
      </ContextMenuContent>
    </ContextMenuPortal>
  </ContextMenuRoot>
</template>
